// src/services/storage/KeyValueStore.ts
import type { ILogger } from '../log/types';
import type { StorageAdapter } from './types';

export class KeyValueStore<K extends string> {
  private readonly ns: string;
  private readonly adapter: StorageAdapter;
  private readonly logger?: ILogger;
  private enableVerboseLogging = false;

  constructor(ns: string, adapter: StorageAdapter, logger?: ILogger) {
    this.ns = ns;
    this.adapter = adapter;
    this.logger = logger;
  }

  private k(key: K): string {
    return `${this.ns}:${key}`;
  }

  private trace(msg: string, data: unknown) {
    if (this.enableVerboseLogging) {
      this.logger?.trace(msg, data);
    }
  }

  setString(key: K, value: string) {
    const namespaced = this.k(key);
    this.trace('KeyValueStore.setString', { key: namespaced, value });
    this.adapter.setItem(namespaced, value);
  }

  getString(key: K) {
    const namespaced = this.k(key);
    const value = this.adapter.getItem(namespaced);
    this.trace('KeyValueStore.getString', { key: namespaced, value });
    return value;
  }

  remove(key: K) {
    const namespaced = this.k(key);
    this.trace('KeyValueStore.remove', { key: namespaced });
    this.adapter.removeItem(namespaced);
  }

  setJSON<T>(key: K, value: T) {
    this.trace('KeyValueStore.setJSON', { key: this.k(key), value });
    this.setString(key, JSON.stringify(value));
  }

  getJSON<T>(key: K): T | null {
    const s = this.getString(key);
    if (!s) {
      this.trace('KeyValueStore.getJSON (miss)', { key: this.k(key) });
      return null;
    }
    try {
      const parsed = JSON.parse(s) as T;
      this.trace('KeyValueStore.getJSON (hit)', { key: this.k(key), value: parsed });
      return parsed;
    } catch (err) {
      this.logger?.warn('KeyValueStore.getJSON (parse error)', { key: this.k(key), raw: s, error: err });
      return null;
    }
  }

  setWithTTL<T>(key: K, value: T, ttlMs: number) {
    const exp = Date.now() + ttlMs;
    this.trace('KeyValueStore.setWithTTL', { key: this.k(key), value, ttlMs, exp });
    this.setJSON(key, { value, exp });
  }

  getWithTTL<T>(key: K): T | null {
    const obj = this.getJSON<{ value: T; exp: number }>(key);
    if (!obj) {
      this.logger?.trace('KeyValueStore.getWithTTL (miss)', { key: this.k(key) });
      return null;
    }
    if (obj.exp && Date.now() > obj.exp) {
      this.trace('KeyValueStore.getWithTTL (expired)', { key: this.k(key), exp: obj.exp });
      this.remove(key);
      return null;
    }
    this.trace('KeyValueStore.getWithTTL (hit)', { key: this.k(key), value: obj.value });
    return obj.value;
  }
}
